import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  storyblokEditable,
  StoryblokComponent,
  useStoryblok,
} from "@storyblok/react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { useDelayedLoading } from "../../helpers/useDelayedLoading";
import { optimizeImage } from "../../helpers/storyblokImage";
import type {
  PageStoryblok,
  HeroDeepdiveStoryblok,
} from "../../../component-types-sb";
import "./book.scss";

// Singleton-stories die puur als databron dienen en nooit als eigen
// pagina bezocht mogen worden.
const RESERVED_SLUGS = ["globals"];

const Page = () => {
  const location = useLocation();
  const currentSlug = location.pathname.replace(/^\/+|\/+$/g, "") || "home";
  const isReserved = RESERVED_SLUGS.includes(currentSlug);

  const story = useStoryblok(currentSlug, {
    version: "draft",
    resolve_links: "url",
  });
  // useStoryblok begint met een leeg object ({}), niet null/undefined, dus
  // we bepalen "geladen" aan de hand van story.content i.p.v. story zelf.
  const hasContent = Boolean(story?.content) && !isReserved;

  // Blijf de content van de vorige pagina tonen terwijl de nieuwe nog
  // laadt, i.p.v. leeg te flitsen tussen twee pagina's — alleen de
  // allereerste load van de site laat nog even niets zien.
  const [displayContent, setDisplayContent] = useState<PageStoryblok | null>(
    null
  );
  useEffect(() => {
    if (hasContent) setDisplayContent(story.content as PageStoryblok);
  }, [hasContent, story]);

  const showLoading = useDelayedLoading(!hasContent && !displayContent, 200);
  const showNotFound = useDelayedLoading(!hasContent, 3000);

  if (isReserved || showNotFound) return <div>Pagina niet gevonden</div>;

  const content = hasContent
    ? (story.content as PageStoryblok)
    : displayContent;
  if (!content) {
    return showLoading ? <div>Loading...</div> : null;
  }

  // Een pagina is een "deepdive" (boek/training-detail met afbeelding
  // links) wanneer het eerste blok in de body een heroDeepdive is —
  // dat is al de bestaande, content-gedreven marker, geen aparte route.
  const heroBlok = content.body?.[0];
  const isDeepdive = heroBlok?.component === "heroDeepdive";

  if (isDeepdive) {
    const deepdiveBlok = heroBlok as HeroDeepdiveStoryblok;
    const image = deepdiveBlok.image;
    const path = image ? image.filename : "";
    const alt = image ? image.alt : "";
    const fullscreen = deepdiveBlok.fullscreen;
    const bgColorLeft = deepdiveBlok.bgLeftSide;

    return (
      <main {...storyblokEditable(content)} className="page-book">
        <div
          className={`left ${bgColorLeft === "orange" ? "orange" : "pink"} `}
        >
          {path ? (
            <LazyLoadImage
              className={`${fullscreen ? "fullscreen-image" : "image"}`}
              src={optimizeImage(path, 800)}
              alt={alt}
              effect="opacity"
              width={100 + "%"}
              height={100 + "%"}
              wrapperClassName="wrapper"
            />
          ) : (
            ""
          )}
        </div>
        <div className="right">
          {content.body.map((blok) => (
            <StoryblokComponent blok={blok} key={blok._uid} />
          ))}
        </div>
      </main>
    );
  }

  return (
    <main className="px-4">
      {content.body?.map((blok) => (
        <StoryblokComponent blok={blok} key={blok._uid} />
      ))}
    </main>
  );
};

export default Page;
